import { Permission, type Channel, type Me, type VoiceParticipant } from '@novachat/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, MessagesSquare, Settings, ChevronUp, DoorOpen, Hash, Search, Headphones, HeadphoneOff, LogOut, Mic, MicOff, Monitor, PhoneOff, Plus, Lock, Moon } from 'lucide-react';
import { useRef, useState } from 'react';
import { firstName } from '../lib/avatar';
import { client } from '../lib/client';
import { errorMessage } from '../lib/errors';
import { resetChat, useChat } from '../state/chat';
import { useLive } from '../state/live';
import { useVoice } from '../state/voice';
import { showThreadsPage, useThreadPanel } from '../state/thread';
import { useDirectory } from '../lib/directory';
import { plainText } from '../lib/mentions';
import { clock, dayLabel } from '../lib/time';
import { FLOW, useConnectionHealth } from '../state/health';
import { LEAVE } from './CallBar';
import { setDoNotDisturb } from '../state/presence';
import { Avatar, presenceText } from './Avatar';
import { ProfileButton } from './ProfileCard';
import { ChannelIcon } from './ChannelIcon';
import { CreateRoomDialog } from './CreateRoomDialog';
import { NewDmDialog } from './NewDmDialog';
import { Modal } from './RoomDialogs';
import { SEARCH_KEY, useSearch } from './SearchDialog';
import { useSettings } from './SettingsDialog';
import { Logo, Wordmark } from './Logo';
import { useDismiss } from '../lib/dismiss';

export function useChannels() {
  return useQuery({ queryKey: ['channels'], queryFn: () => client.api.channels() });
}

export function Sidebar({ user, className = '' }: { user: Me; className?: string }) {
  const { data: channels = [], isPending } = useChannels();
  const [creating, setCreating] = useState<'channel' | 'dm' | null>(null);
  const reads = useChat((s) => s.reads);
  const texts = channels.filter((c) => c.kind !== 'dm');
  // Most recent conversation first.
  const dms = channels
    .filter((c) => c.kind === 'dm')
    .sort((a, b) => (reads[b.id]?.lastMessageId ?? '').localeCompare(reads[a.id]?.lastMessageId ?? '') || (a.peer?.name ?? '').localeCompare(b.peer?.name ?? '', 'tr'));
  const canCreate = user.permissions.includes(Permission.ChannelsCreate);

  return (
    <aside aria-label="Kenar çubuğu" className={`flex w-full flex-none flex-col bg-surface-0 md:w-[272px] md:border-r md:border-line ${className}`}>
      <div className="flex items-center gap-2.5 px-4 pt-[max(16px,env(safe-area-inset-top))] pb-3">
        <Logo size={34} />
        <div className="min-w-0 flex-1">
          <Wordmark className="block text-[17px] leading-tight" />
          <span className="text-xs text-fg-3">Novadan</span>
        </div>
        <ConnectionBadge />
      </div>
      <button
        type="button"
        onClick={() => useSearch.getState().show()}
        className="mx-3 mb-1 flex h-9 items-center gap-2 rounded-md border border-line bg-surface-1 px-3 text-left text-[13px] text-fg-3 hover:border-line-strong hover:text-fg-2 max-md:h-10"
      >
        <Search size={15} aria-hidden="true" />
        <span className="flex-1">Mesajlarda ara</span>
        <kbd className="rounded-xs border border-line-strong px-1.5 py-px font-mono text-[11px] max-md:hidden">{SEARCH_KEY}</kbd>
      </button>

      <nav aria-label="Kanallar" className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
        <FollowedThreads channels={[...texts, ...dms]} />
        <SectionTitle title="KANALLAR" action={canCreate ? { label: 'Kanal oluştur', onClick: () => setCreating('channel') } : null} />
        {isPending && <p className="px-2.5 text-[13px] text-fg-3">Yükleniyor…</p>}
        {!isPending && texts.length === 0 && (
          <p className="px-2.5 text-[13px] leading-normal text-fg-3">Henüz kanal yok.{canCreate ? ' Sağdaki + ile ilkini aç.' : ''}</p>
        )}
        {texts.map((channel) => (
          <ChannelEntry key={channel.id} channel={channel} />
        ))}
        <ChannelShelf shelf="left" />
        <ChannelShelf shelf="archived" />

        <SectionTitle title="DİREKT MESAJLAR" action={{ label: 'Yeni mesaj', onClick: () => setCreating('dm') }} />
        {!isPending && dms.length === 0 && <p className="px-2.5 text-[13px] leading-normal text-fg-3">Birine yazmak için + ile kişi seç.</p>}
        {dms.map((channel) => (
          <ChannelEntry key={channel.id} channel={channel} />
        ))}
      </nav>

      <ConnectedCard channels={texts} />
      <UserCard user={user} />
      {creating === 'channel' && <CreateRoomDialog me={user} onClose={() => setCreating(null)} />}
      {creating === 'dm' && <NewDmDialog me={user} onClose={() => setCreating(null)} />}
    </aside>
  );
}

/**
 * Channels outside your list, behind one quiet entry under it; only shown when there are some.
 * "Ayrıldığın kanallar": public channels you left or were removed from, to join again.
 * "Arşivlenen kanallar": archived channels your roles let you open again.
 */
const SHELVES = {
  left: {
    title: 'Ayrıldığın kanallar',
    hint: 'Ayrıldığın ya da çıkarıldığın açık kanallar. Katılınca kanal listene geri gelir.',
    icon: DoorOpen,
    action: 'Katıl',
    actionLabel: (name: string) => `${name} kanalına katıl`,
    load: () => client.api.leftChannels(),
    act: (id: string) => client.api.joinChannel(id),
  },
  archived: {
    title: 'Arşivlenen kanallar',
    hint: 'Geri açabileceğin arşivlenmiş kanallar. Geri açınca mesajlarıyla birlikte üyelerinin listesine döner.',
    icon: Archive,
    action: 'Geri aç',
    actionLabel: (name: string) => `${name} kanalını geri aç`,
    load: () => client.api.archivedChannels(),
    act: (id: string) => client.api.restoreChannel(id),
  },
} as const;

function ChannelShelf({ shelf }: { shelf: keyof typeof SHELVES }) {
  const { title, hint, icon: Icon, action, actionLabel, load, act } = SHELVES[shelf];
  const queries = useQueryClient();
  const { data: channels = [] } = useQuery({ queryKey: ['channels', shelf], queryFn: load });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (channels.length === 0) return null;
  async function run(channel: Channel, close: () => void) {
    setError(null);
    setBusy(channel.id);
    try {
      await act(channel.id);
      await queries.invalidateQueries({ queryKey: ['channels'] });
      useLive.getState().select(channel.id);
      close();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="nc-press flex w-full items-center gap-2 rounded-sm border-0 bg-transparent px-2.5 py-[7px] text-left text-[13px] text-fg-3 hover:bg-surface-2 hover:text-fg-2 max-md:py-3"
      >
        <Icon size={16} aria-hidden="true" />
        <span className="flex-1">{title}</span>
        <span className="text-xs">{channels.length}</span>
      </button>
      {open && (
        <Modal title={title} onClose={() => setOpen(false)}>
          {(close) => (
            <div className="flex flex-col gap-3">
              <p className="m-0 text-[13px] leading-normal text-fg-3">{hint}</p>
              <ul className="m-0 flex list-none flex-col gap-1 p-0">
                {channels.map((channel) => (
                  <li key={channel.id} className="flex items-center gap-2.5 rounded-sm px-2 py-1.5 text-sm">
                    <ChannelIcon channel={channel} size={17} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-semibold">{channel.name}</span>
                      {channel.topic && <span className="block truncate text-xs text-fg-3">{channel.topic}</span>}
                    </span>
                    <button
                      type="button"
                      aria-label={actionLabel(channel.name)}
                      disabled={busy !== null}
                      onClick={() => void run(channel, close)}
                      className="nc-press nc-cta h-8 flex-none rounded-sm border-0 bg-brand-500 px-3 text-[13px] font-bold text-white disabled:opacity-70"
                    >
                      {action}
                    </button>
                  </li>
                ))}
              </ul>
              {error && <p role="alert" className="m-0 text-[13px] text-[#EE9191]">{error}</p>}
            </div>
          )}
        </Modal>
      )}
    </>
  );
}

/** "Dallar": the threads you follow, newest reply first, with their unread replies. Only when you follow one. */
function FollowedThreads({ channels }: { channels: Channel[] }) {
  const followed = useChat((s) => s.followed);
  const people = useDirectory();
  const onPage = useThreadPanel((s) => s.page);
  const list = Object.values(followed).sort((a, b) => (b.root.thread?.lastReplyAt ?? b.root.createdAt).localeCompare(a.root.thread?.lastReplyAt ?? a.root.createdAt));
  if (list.length === 0) return null;
  const unread = list.reduce((sum, thread) => sum + thread.unread, 0);
  return (
    <>
      <button
        type="button"
        onClick={showThreadsPage}
        aria-label={`Dallar${unread > 0 ? `, ${unread} yeni yanıt` : ''}`}
        aria-current={onPage ? 'page' : undefined}
        className={`nc-press mt-2 flex w-full items-center gap-2 rounded-sm border-0 px-2.5 py-[7px] text-left text-[14px] hover:bg-surface-2 hover:text-fg-1 max-md:py-3 ${onPage ? 'bg-surface-3 text-fg-1' : 'bg-transparent text-fg-2'}`}
      >
        <MessagesSquare size={17} aria-hidden="true" />
        <span className={`flex-1 ${unread > 0 ? 'font-bold text-fg-1' : ''}`}>Dallar</span>
        {unread > 0 && <span className="grid h-5 min-w-5 place-items-center rounded-full bg-brand-500 px-1.5 text-[11px] font-bold text-white">{unread >= 100 ? '99+' : unread}</span>}
      </button>
    </>
  );
}

function SectionTitle({ title, action }: { title: string; action: { label: string; onClick: () => void } | null }) {
  return (
    <div className="flex items-center justify-between px-2.5 pt-4 pb-1.5 first:pt-3.5">
      <span className="font-heading text-[11px] font-bold tracking-[0.08em] text-fg-3">{title}</span>
      {action && (
        <button
          type="button"
          aria-label={action.label}
          title={action.label}
          onClick={action.onClick}
          className="nc-press grid size-6 place-items-center rounded-xs border-0 bg-transparent text-fg-3 hover:bg-surface-2 hover:text-fg-1 max-md:size-8"
        >
          <Plus size={16} />
        </button>
      )}
    </div>
  );
}

/** A channel or a DM: unread count, and who is in the channel's call. */
function ChannelEntry({ channel }: { channel: Channel }) {
  const selected = useLive((s) => s.selectedChannelId === channel.id);
  const unread = useChat((s) => s.reads[channel.id]?.unread ?? 0);
  const mentions = useChat((s) => s.reads[channel.id]?.mentions ?? 0);
  const presence = useLive((s) => (channel.peer ? (s.presence[channel.peer.id] ?? 'offline') : undefined));
  const participants = useLive((s) => s.voice[channel.id]);
  const joined = useVoice((s) => s.channelId === channel.id);
  const speaking = useVoice((s) => (s.channelId === channel.id ? s.speaking : null));
  const name = channel.kind === 'dm' ? (channel.peer?.name ?? 'Direkt mesaj') : channel.name;
  const showUnread = unread > 0 && !selected;
  const live = (participants?.length ?? 0) > 0;
  return (
    <div>
      <button
        type="button"
        onClick={() => useLive.getState().select(channel.id)}
        aria-current={selected ? 'page' : undefined}
        className={`nc-press flex w-full items-center gap-2 rounded-sm border-0 px-2.5 text-left text-sm max-md:text-[15px] ${channel.kind === 'dm' ? 'py-1.5 max-md:py-2.5' : 'py-[7px] max-md:py-3'} ${
          selected ? 'bg-surface-2 font-semibold text-fg-1' : showUnread ? 'bg-transparent font-semibold text-fg-1 hover:bg-surface-2' : 'bg-transparent text-fg-2 hover:bg-surface-2 hover:text-fg-1'
        }`}
      >
        {channel.kind === 'dm' && channel.peer ? (
          <Avatar id={channel.peer.id} name={channel.peer.name} size={24} {...(presence ? { presence } : {})} className="mr-0.5" />
        ) : (
          <ChannelIcon channel={channel} size={17} />
        )}
        <span className="min-w-0 flex-1 truncate">{name}</span>
        {live && (
          // A call here: a green pill with rings spreading from its dot, like the call bar.
          <span className="flex flex-none items-center gap-1.5 rounded-full bg-green-700 py-0.5 pr-2 pl-1.5 text-[11px] font-bold text-white" role="img" aria-label={`Görüşme var, ${participants!.length} kişi`}>
            <span className="nc-ripple scale-[0.7]" aria-hidden="true" />
            {participants!.length}
          </span>
        )}
        {joined && <span className="sr-only">(görüşmedesin)</span>}
        {showUnread && (
          <span
            className={`grid h-[18px] min-w-5 place-items-center rounded-[9px] px-1.5 text-[11px] font-bold ${
              channel.notifyLevel === 'mute' ? 'bg-surface-3 text-fg-2' : mentions > 0 ? 'bg-red-400 text-white' : 'bg-brand-500 text-white'
            }`}
            aria-label={`${unread >= 100 ? '99+' : unread} okunmamış mesaj${mentions > 0 ? `, ${mentions} bahsetme` : ''}`}
          >
            {unread >= 100 ? '99+' : unread}
          </span>
        )}
      </button>
      {live && (
        <ul className="m-0 flex list-none flex-col gap-px py-0.5 pr-0 pb-1.5 pl-[26px]">
          {participants!.map((p) => (
            <ParticipantRow key={p.userId} participant={p} speaking={speaking?.has(p.userId) ?? false} />
          ))}
        </ul>
      )}
    </div>
  );
}

function ConnectionBadge() {
  const status = useLive((s) => s.status);
  if (status === 'open') return null;
  return (
    <span className="rounded-xs bg-surface-3 px-2 py-1 text-[11px] font-semibold text-fg-2" role="status">
      {status === 'connecting' ? 'Bağlanıyor…' : 'Çevrimdışı'}
    </span>
  );
}

function ParticipantRow({ participant, speaking }: { participant: VoiceParticipant; speaking: boolean }) {
  return (
    <li className="flex items-center gap-2 px-2 py-1 text-[13px]">
      <ProfileButton user={{ id: participant.userId, name: participant.name }} className="flex min-w-0 flex-1 items-center gap-2 rounded-sm hover:text-fg-1">
        <Avatar id={participant.userId} name={participant.name} size={22} speaking={speaking} />
        <span className={`min-w-0 flex-1 truncate ${speaking ? 'text-fg-1' : 'text-fg-2'}`}>{firstName(participant.name)}</span>
      </ProfileButton>
      {participant.screenSharing && (
        <span className="animate-chip-pulse rounded-[5px] bg-brand-500 px-1.5 py-0.5 text-[10px] font-bold tracking-[0.06em] text-white">CANLI</span>
      )}
      {!participant.micOn && <MicOff size={14} className="flex-none text-fg-3" aria-label="Mikrofon kapalı" />}
    </li>
  );
}

/** "Ses bağlı" card: whose call you are in, with quick controls (design A layout). */
function ConnectedCard({ channels }: { channels: Channel[] }) {
  const voice = useVoice();
  const health = useConnectionHealth();
  if (!voice.channelId) return null;
  const room = channels.find((c) => c.id === voice.channelId);
  // Coloured like the call bar: green, yellow when the connection weakens, red when it is poor or cut.
  const label = health.text ?? (voice.connection === 'connected' ? 'Ses bağlı' : voice.connection === 'reconnecting' ? 'Yeniden bağlanıyor…' : 'Bağlanıyor…');
  const iconButton = 'nc-press grid size-8 place-items-center rounded-sm border-0';

  return (
    <div className={`${FLOW[health.level]} mx-2 mb-2 flex items-center gap-2 rounded-md px-3 py-2.5 text-white`}>
      <button type="button" onClick={() => useLive.getState().select(voice.channelId)} className="min-w-0 flex-1 border-0 bg-transparent p-0 text-left">
        <span className="block truncate text-[13px] font-semibold text-white" title={label}>
          {label}
        </span>
        <span className="block truncate text-xs text-white/85">{room?.name ?? '…'}</span>
      </button>
      <button
        type="button"
        onClick={() => void voice.toggleMic()}
        aria-label={voice.micOn ? 'Mikrofonu kapat' : 'Mikrofonu aç'}
        aria-pressed={voice.micOn}
        className={`${iconButton} text-white ${voice.micOn ? 'bg-white/25' : 'bg-black/25'}`}
      >
        {voice.micOn ? <Mic size={16} /> : <MicOff size={16} />}
      </button>
      <button
        type="button"
        onClick={voice.toggleDeafen}
        aria-label={voice.deafened ? 'Sesi aç' : 'Sesi kapat'}
        aria-pressed={voice.deafened}
        className={`${iconButton} text-white ${voice.deafened ? 'bg-black/25' : 'bg-white/15'}`}
      >
        {voice.deafened ? <HeadphoneOff size={16} /> : <Headphones size={16} />}
      </button>
      {voice.screenOn && <Monitor size={16} className="text-brand-400" aria-label="Ekranını paylaşıyorsun" />}
      <button type="button" onClick={() => void voice.leave()} aria-label="Görüşmeden ayrıl" className={`${iconButton} ${LEAVE[health.level]}`}>
        <PhoneOff size={16} />
      </button>
    </div>
  );
}

function UserCard({ user }: { user: Me }) {
  const presence = useLive((s) => s.presence[user.id]) ?? 'online';
  const [menu, setMenu] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  const menuPanel = useRef<HTMLDivElement>(null);
  useDismiss(menu, () => setMenu(false), menuPanel, menuButton);
  return (
    <div className="relative flex items-center gap-1 border-t border-line px-2 pt-2 pb-[max(10px,env(safe-area-inset-bottom))]">
      {/* Like Discord: the name opens status and sign-out, the gear opens settings. */}
      <button
        ref={menuButton}
        type="button"
        aria-label="Durum ve oturum"
        aria-expanded={menu}
        onClick={() => setMenu((m) => !m)}
        className="flex min-w-0 flex-1 items-center gap-2.5 rounded-md border-0 bg-transparent px-2 py-1.5 text-left text-fg-1 hover:bg-surface-2"
      >
        <Avatar id={user.id} name={user.name} size={32} presence={presence} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold">{user.name}</span>
          <span className="block text-xs text-fg-3">{presenceText(presence)}</span>
        </span>
        <ChevronUp size={15} className={`flex-none text-fg-3 transition-transform ${menu ? '' : 'rotate-180'}`} aria-hidden="true" />
      </button>
      <button
        type="button"
        aria-label="Ayarlar"
        title="Ayarlar"
        onClick={() => useSettings.getState().show()}
        className="nc-press grid size-9 flex-none place-items-center rounded-sm border-0 bg-transparent text-fg-2 hover:bg-surface-2 hover:text-fg-1"
      >
        <Settings size={18} />
      </button>
      {menu && (
        <div ref={menuPanel} role="menu" className="absolute bottom-14 left-2 z-20 flex w-56 animate-enter flex-col rounded-md border border-line-strong bg-surface-2 p-1 shadow-2xl">
          <MenuItem
            icon={<Moon size={15} />}
            label={presence === 'dnd' ? 'Rahatsız etmeyini kapat' : 'Rahatsız etmeyin'}
            onClick={() => {
              setDoNotDisturb(presence !== 'dnd');
              setMenu(false);
            }}
          />
          <MenuItem
            icon={<LogOut size={15} />}
            label="Çıkış yap"
            danger
            onClick={() => {
              void useVoice.getState().leave();
              resetChat();
              void client.session.logout();
            }}
          />
        </div>
      )}
    </div>
  );
}

function MenuItem({ icon, label, onClick, danger = false }: { icon: React.ReactNode; label: string; onClick: () => void; danger?: boolean }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={`flex items-center gap-2.5 rounded-sm border-0 bg-transparent px-2.5 py-2 text-left text-[13.5px] hover:bg-surface-3 ${danger ? 'text-[#EE9191]' : 'text-fg-1'}`}
    >
      {icon}
      {label}
    </button>
  );
}
